/**
 * Machine `code` values the backend adds to its existing error body shape for
 * the new subscription/entitlement guards (SNAP-2241 contract):
 * - createTransaction: PRODUCT_NOT_ENABLED (403), SUBSCRIPTION_EXISTS (422),
 *   PAYMENT_PENDING (409: already paid a checkout whose webhook has not arrived yet),
 *   TRIAL_ALREADY_USED (422: a trial price for a company that already had a subscription)
 * - updateSubscription: PRICE_PRODUCT_MISMATCH (422)
 * - PUT /company: PRODUCT_NOT_PAID (403)
 */
export const BILLING_ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_EXISTS: 'Your company already has an active subscription. Contact us to add or change products.',
  PRODUCT_NOT_ENABLED: "This plan isn't available for your company yet. Contact us to enable it.",
  PRICE_PRODUCT_MISMATCH:
    'This plan belongs to a different product than your current subscription. Contact us to switch products.',
  PRODUCT_NOT_PAID: "Your company hasn't paid for this product yet.",
  PAYMENT_PENDING:
    'Your payment is being confirmed. This usually takes a minute or two, so there is no need to pay again.',
  TRIAL_ALREADY_USED: 'Your company has already used its free trial. Choose a paid plan.',
};

type ErrorResponseLike = { response?: { data?: { code?: string } } } | null | undefined;

/** Returns a readable message for a known billing error `code`, else null. */
export const billingErrorMessage = (error: ErrorResponseLike): string | null => {
  const code = error?.response?.data?.code;
  return (code && BILLING_ERROR_MESSAGES[code]) || null;
};

/** createTransaction 409: the company already paid and only the webhook is missing. Never offer Subscribe again. */
export const isPaymentPendingError = (error: ErrorResponseLike): boolean =>
  error?.response?.data?.code === 'PAYMENT_PENDING';
